import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlowCard } from '../components/GlowCard.js';

interface PlatformConnection {
  id: string;
  platform: 'tiktok' | 'youtube' | 'instagram' | 'facebook' | 'linkedin';
  platformUserId: string | null;
  platformUsername: string | null;
  expiresAt: string | null;
  createdAt: string;
}

function StatusBadge({ connected }: { connected: boolean }) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        connected
          ? 'bg-green-500/10 text-green-400 ring-1 ring-green-500/20'
          : 'bg-white/5 text-white/60',
      ].join(' ')}
    >
      <span
        className={['h-1.5 w-1.5 rounded-full', connected ? 'bg-green-500' : 'bg-gray-400'].join(' ')}
      />
      {connected ? 'Connected' : 'Not connected'}
    </span>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.79 1.52V6.75a4.85 4.85 0 01-1.02-.06z" />
    </svg>
  );
}

function YouTubeIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" aria-hidden="true">
      <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

const PLATFORM_CONFIGS: Array<{
  id: 'tiktok' | 'youtube' | 'instagram' | 'facebook' | 'linkedin';
  label: string;
  sublabel: string;
  bgColor: string;
  Icon: () => JSX.Element;
}> = [
  {
    id: 'tiktok',
    label: 'TikTok',
    sublabel: 'Source platform',
    bgColor: 'bg-black',
    Icon: TikTokIcon,
  },
  {
    id: 'youtube',
    label: 'YouTube',
    sublabel: 'Output platform',
    bgColor: 'bg-red-600',
    Icon: YouTubeIcon,
  },
  {
    id: 'instagram',
    label: 'Instagram',
    sublabel: 'Output platform',
    bgColor: 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400',
    Icon: InstagramIcon,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    sublabel: 'Output platform',
    bgColor: 'bg-blue-600',
    Icon: FacebookIcon,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    sublabel: 'Output platform',
    bgColor: 'bg-sky-700',
    Icon: LinkedInIcon,
  },
];

export default function PlatformConnectionsPage() {
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const successPlatform = searchParams.get('connected');
  const errorMessage = searchParams.get('error');

  useEffect(() => {
    loadConnections();
  }, []);

  async function loadConnections() {
    setLoading(true);
    const result = await api.get<PlatformConnection[]>('/api/platforms');
    if (result.data) setConnections(result.data);
    setLoading(false);
  }

  function handleConnect(platform: string) {
    const apiBase = import.meta.env['VITE_API_URL'] as string ?? 'http://localhost:3000';
    window.location.href = `${apiBase}/api/platforms/${platform}/connect`;
  }

  async function handleDisconnect(id: string) {
    setDisconnecting(id);
    await api.delete(`/api/platforms/${id}`);
    setConnections((prev) => prev.filter((c) => c.id !== id));
    setDisconnecting(null);
  }

  function dismissBanner() {
    setSearchParams({}, { replace: true });
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-white">Platform Connections</h1>
      <p className="mt-1 text-sm text-white/50">
        Connect your social accounts to start building pipelines.
      </p>

      {successPlatform && (
        <div className="mt-4 flex items-start justify-between rounded-lg bg-green-500/10 px-4 py-3 ring-1 ring-green-500/20">
          <p className="text-sm text-green-400">
            <span className="font-medium capitalize">{successPlatform}</span> connected successfully.
          </p>
          <button onClick={dismissBanner} className="ml-4 text-green-400 hover:text-green-300">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
      )}

      {errorMessage && (
        <div className="mt-4 flex items-start justify-between rounded-lg bg-red-500/10 px-4 py-3 ring-1 ring-red-500/20">
          <p className="text-sm text-red-400">
            Connection failed: {decodeURIComponent(errorMessage)}
          </p>
          <button onClick={dismissBanner} className="ml-4 text-red-400 hover:text-red-300">
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="flex h-24 items-center justify-center">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-pix-pink border-t-transparent" />
          </div>
        ) : connections.length === 0 ? (
          <>
            <EmptyState
              icon={
                <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                </svg>
              }
              headline="No platforms connected"
              subtext="Connect TikTok to start ingesting content, then connect your output platforms to distribute."
              cta={{ label: 'Connect TikTok', onClick: () => handleConnect('tiktok') }}
            />
            <GlowCard className="overflow-hidden rounded-xl border border-white/10 bg-pix-deep divide-y divide-white/5">
              {PLATFORM_CONFIGS.map(({ id, label, sublabel, bgColor, Icon }) => {
                const connection = connections.find((c) => c.platform === id);
                return (
                  <div key={id} className="flex items-center justify-between px-5 py-4">
                    <div className="flex items-center gap-4">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${bgColor}`}>
                        <Icon />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{label}</p>
                        {connection?.platformUsername ? (
                          <p className="text-xs text-white/50">@{connection.platformUsername}</p>
                        ) : (
                          <p className="text-xs text-white/40">{sublabel}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge connected={!!connection} />
                      {connection ? (
                        <button
                          onClick={() => handleDisconnect(connection.id)}
                          disabled={disconnecting === connection.id}
                          className="rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-white/60 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        >
                          {disconnecting === connection.id ? 'Disconnecting...' : 'Disconnect'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnect(id)}
                          className="rounded-lg bg-pix-pink px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-pix-pink-dark"
                        >
                          Connect
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </GlowCard>
          </>
        ) : (
          <GlowCard className="overflow-hidden rounded-xl border border-white/10 bg-pix-deep divide-y divide-white/5">
            {PLATFORM_CONFIGS.map(({ id, label, sublabel, bgColor, Icon }) => {
              const connection = connections.find((c) => c.platform === id);
              return (
                <div key={id} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-4">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${bgColor}`}>
                      <Icon />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{label}</p>
                      {connection?.platformUsername ? (
                        <p className="text-xs text-white/50">@{connection.platformUsername}</p>
                      ) : (
                        <p className="text-xs text-white/40">{sublabel}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <StatusBadge connected={!!connection} />

                    {connection ? (
                      <button
                        onClick={() => handleDisconnect(connection.id)}
                        disabled={disconnecting === connection.id}
                        className="rounded-lg border border-white/10 px-3 py-1.5 text-xs font-medium text-white/60 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      >
                        {disconnecting === connection.id ? 'Disconnecting...' : 'Disconnect'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConnect(id)}
                        className="rounded-lg bg-pix-pink px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-pix-pink-dark"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </GlowCard>
        )}
      </div>
    </div>
  );
}
